import { useState, useEffect } from "react";
import {
  // ResourceListActionsMenu — off the toolbar, see the commented-out element below.
  ResourceMonitoringTable,
} from "@/components/resources";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarClock,
  Filter,
  Layers,
  Loader2,
  RefreshCw,
  Search,
  Server,
  X,
} from "lucide-react";
import { useResources } from "@/hooks/useResourceQueries";
import { useProductLines } from "@/hooks/useProductLines";
import { formatDateRangeForAPI } from "@/lib/date-utils";
import { formatDate } from "@/lib/utils";
import {
  ALL_PRODUCT_LINES,
  RESOURCE_EXPIRING_OPTIONS,
  RESOURCE_LIST_STATUS_OPTIONS,
  RESOURCE_TYPE_OPTIONS,
  useResourceFiltersStore,
} from "@/stores/resourceFiltersStore";
import type { ResourceType, ResourceStatus } from "@/types/admin";
import { getPaginationItems } from "@/lib/pagination";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import {
  RESOURCE_COLUMNS,
  useResourceColumnsStore,
} from "@/stores/resourceColumnsStore";

export function ResourceMonitoringPage() {
  // Committed filters, the page and the page size live in a persisted store, so the view an
  // operator set up survives a reload and a walk to the next screen and back.
  const { search, type, status, dateRange, expiringInDays, productLineSlug } =
    useResourceFiltersStore((state) => state.filters);
  const page = useResourceFiltersStore((state) => state.page);
  const pageSize = useResourceFiltersStore((state) => state.pageSize);
  const setFilter = useResourceFiltersStore((state) => state.setFilter);
  const patchFilters = useResourceFiltersStore((state) => state.patchFilters);
  const setPage = useResourceFiltersStore((state) => state.setPage);
  const setPageSize = useResourceFiltersStore((state) => state.setPageSize);
  const clearCache = useResourceFiltersStore((state) => state.clearCache);
  // Column visibility lives in its own store so the menu here and the table below share it without
  // props. Subscribing to `hidden` (the value that changes) and deriving from it - see the same
  // note in ResourceMonitoringTable.
  const hiddenColumns = useResourceColumnsStore((state) => state.hidden);
  const toggleColumn = useResourceColumnsStore((state) => state.toggle);
  const showAllColumns = useResourceColumnsStore((state) => state.showAll);
  // Computed inside the selector, not after it: subscribing to the boolean re-renders this page
  // only when it actually flips, and does not depend on some other selector happening to
  // subscribe to `filters` for the value to stay current.
  const hasActiveFilters = useResourceFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  // Only the half-typed text is local: it changes on every keystroke, and committing that to the
  // store would write sessionStorage per character and re-run the query per character.
  const [searchInput, setSearchInput] = useState(search);

  // Whether the Filters popover is open. Held here only so the product lines are not requested
  // before someone opens it.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Incremented by Refresh so the table clears its checkboxes — see ResourceMonitoringTable.
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  // Debounce free-text search so filtering doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      // Guarded because this also runs on mount, where committing the restored value would be a
      // no-op write that still sends the operator back to page 1.
      if (searchInput !== search) {
        setFilter("search", searchInput);
      }
    }, 750);
    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  // Same "both ends or neither" rule the backend enforces — an unbounded from/to isn't a real
  // range, so half a picked range is not sent as a filter.
  const { fromDate: createdFrom, toDate: createdTo } =
    dateRange.from && dateRange.to
      ? formatDateRangeForAPI(dateRange.from, dateRange.to, false)
      : { fromDate: undefined, toDate: undefined };

  const isFilteringByProductLine = productLineSlug !== ALL_PRODUCT_LINES;

  const filters = {
    search: search || undefined,
    type,
    status,
    createdFrom,
    createdTo,
    expiringInDays: expiringInDays ?? undefined,
    productLineSlug: isFilteringByProductLine ? productLineSlug : undefined,
    page,
    pageSize,
  };

  // `isLoading` is the first load only — the one render pass with no rows to show yet. Every
  // later fetch keeps the previous page on screen (see `placeholderData` on the hook), so
  // `isFetching` alone cannot tell "still arriving" apart from "came back empty".
  const {
    data: response,
    isLoading,
    isFetching,
    isPlaceholderData,
    error,
    refetch,
  } = useResources(filters);

  // What the paging controls are held for: the operator's own change of page, page size or filter,
  // still in flight. That is exactly what `isPlaceholderData` reports — rows from the previous key
  // kept on screen while the new one loads. Holding them on `isFetching` also caught the 60s poll
  // the "Restorable" view runs, which refetches the SAME key over rows that did not move, and made
  // Previous/Next go dead for a moment every minute.
  const isSwitchingView = isPlaceholderData;

  /**
   * Picking an expiry window is a whole question, not one more narrowing: "what is about to lapse
   * that I should warn someone about?" So it also sets the two filters that would otherwise answer
   * it wrongly, in a single commit rather than three.
   *
   * Type goes to virtual machines because a VM's own disk and network rows carry the same expiry
   * date and would triple a list meant to be scanned in one glance — and the VM is the thing an
   * operator renews.
   *
   * The created-at range is cleared because it is the filter most likely to hide the exact rows
   * being looked for: the screen opens on the last six months, and a machine created a year ago is
   * both invisible under that default and the likeliest one to be lapsing now.
   *
   * Both stay clearable from their own chips afterwards — this sets a starting point, it does not
   * lock anything.
   */
  const handleExpiringChange = (value: string) => {
    const days = value === "any" ? null : Number(value);

    if (days === null) {
      setFilter("expiringInDays", null);
      return;
    }

    patchFilters({
      expiringInDays: days,
      type: "VirtualMachine",
      dateRange: {},
    });
  };

  /**
   * Only virtual machines record a product line, so a line picked while Type sits on disks or
   * networks could only ever answer with an empty table. Picking one therefore moves Type to
   * virtual machines in the same commit; clearing it leaves Type where the operator put it.
   */
  const handleProductLineChange = (slug: string) => {
    if (slug === ALL_PRODUCT_LINES) {
      setFilter("productLineSlug", ALL_PRODUCT_LINES);
      return;
    }

    patchFilters({ productLineSlug: slug, type: "VirtualMachine" });
  };

  // The lines on offer are the catalogue's own, by slug: the slug is what a machine records and
  // what the backend filters on, and it can be renamed - or a line added - in the Product Lines
  // screen. Every line, on sale or not: a machine ordered on a line that was later switched off
  // still runs on it. Asked for only when it is about to be read - the popover is open, or a line
  // is already picked and its chip needs the name - so opening the list costs no extra request.
  const productLinesQuery = useProductLines(
    { page: 1, limit: 100 },
    { enabled: filtersOpen || isFilteringByProductLine },
  );
  const productLines = productLinesQuery.data?.data ?? [];
  const pickedProductLine = productLines.find(
    (line) => line.slug === productLineSlug,
  );

  // The chip names the line the way the picker does ("Eco"), not by the slug that is sent ("eco").
  // The slug stands in while the catalogue is loading, and for a line it no longer lists.
  const productLineLabel = pickedProductLine?.name ?? productLineSlug;

  // Refresh is the screen's "start over": it drops every filter, the page size and the cached copy
  // in sessionStorage, then reloads. The two text boxes hold their own copy of what is being typed,
  // so clearing the store alone would leave the old term sitting in the input.
  const handleRefresh = () => {
    setSearchInput("");
    clearCache();
    // The table's row selection is part of "everything" too: leaving 16 rows ticked after a reset
    // means the next Delete/Stop acts on a set the operator chose against the previous filters.
    setSelectionResetKey((key) => key + 1);
    void refetch();
  };

  // A term typed but not yet committed: the debounce has not fired, so nothing is fetching and the
  // table still shows the previous result. The spinner in the search box reads from this.
  const isSearchPending = searchInput !== search;

  const resources = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;
  const currentPage = response?.pagination?.currentPage || 1;
  const totalCount = response?.pagination?.totalItems || 0;

  // Only take the screen over when there is genuinely nothing to show. `useResources` keeps the
  // previous page on screen through `placeholderData`, so a refetch that fails - a token refresh
  // losing a race, a blip on one filter change - used to replace a working table AND the whole
  // filter bar with a "Retry" card. The operator then had to set their filters up again to see
  // rows that were still in the cache a moment earlier. With rows in hand the failure is reported
  // in a banner above the table instead, and Refresh/Retry are one click away either way.
  if (error && !response) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-destructive-ink">Failed to load resources</p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the card, not the viewport: `fixed inset-0` centred this on the screen, so
            it hovered in whatever blank space happened to be mid-window rather than over the rows
            it describes. Same change as the payments list. */}
        {isFetching && !isLoading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="w-full sm:w-[360px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                {/* The debounce window has no isFetching to report, so the box says so itself:
                    a term typed but not yet committed leaves the table showing the previous
                    result, and without this the screen sits motionless for three quarters of a
                    second after every keystroke and reads as frozen. */}
                {isSearchPending && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
                <Input
                  placeholder="Search VM name, UUID, ID, IP, email, phone"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  // React withholds onChange while an IME composition is open — which is the
                  // whole time a Vietnamese Telex/VNI method is assembling a word — so the term
                  // only reached state once a space closed the composition, and the search
                  // appeared to need a trailing space to run at all.
                  onCompositionEnd={(e) =>
                    setSearchInput(e.currentTarget.value)
                  }
                  className="pl-10 pr-9"
                />
              </div>
            </div>

            {/* Off the toolbar since 2026-10-09: its one item, "Update resource fields", is now a
                card on the Helper page (sidebar: Configuration → Helper). To bring the menu back, uncomment
                this element and its import above; it goes straight after the search box, ahead of
                the chips and the icon cluster. */}
            {/* <ResourceListActionsMenu /> */}

            <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:self-center sm:justify-end">
              {search && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Search: {search}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      setFilter("search", "");
                    }}
                    aria-label="Clear search filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {type !== "All" && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Type:{" "}
                  {RESOURCE_TYPE_OPTIONS.find((o) => o.value === type)?.label}
                  <button
                    type="button"
                    onClick={() => {
                      setFilter("type", "All");
                    }}
                    aria-label="Clear type filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {status !== "Active" && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Status:{" "}
                  {
                    RESOURCE_LIST_STATUS_OPTIONS.find((o) => o.value === status)
                      ?.label
                  }
                  <button
                    type="button"
                    onClick={() => {
                      setFilter("status", "Active");
                    }}
                    aria-label="Clear status filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {isFilteringByProductLine && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  <Layers className="h-3 w-3" />
                  Product line: {productLineLabel}
                  <button
                    type="button"
                    onClick={() => {
                      setFilter("productLineSlug", ALL_PRODUCT_LINES);
                    }}
                    aria-label="Clear product line filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {expiringInDays !== null && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  <CalendarClock className="h-3 w-3" />
                  {
                    RESOURCE_EXPIRING_OPTIONS.find(
                      (o) => o.value === expiringInDays,
                    )?.label
                  }
                  <button
                    type="button"
                    onClick={() => {
                      setFilter("expiringInDays", null);
                    }}
                    aria-label="Clear expiring-soon filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {dateRange.from && dateRange.to && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Created: {formatDate(dateRange.from)} –{" "}
                  {formatDate(dateRange.to)}
                  <button
                    type="button"
                    onClick={() => {
                      setFilter("dateRange", {});
                    }}
                    aria-label="Clear created date filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>

            <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
              {/* Icon only, so the accessible name has to come from somewhere: `title` gives the
                  hover tooltip, `aria-label` gives screen readers the same words. The comment sits
                  outside PopoverTrigger on purpose: `asChild` forwards to a single child. */}
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filters"
                  aria-label="Filters"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="resourceExpiringFilter">Expiring Soon</Label>
                  <Select
                    value={
                      expiringInDays === null ? "any" : String(expiringInDays)
                    }
                    onValueChange={handleExpiringChange}
                  >
                    <SelectTrigger id="resourceExpiringFilter">
                      <SelectValue placeholder="Any expiry date" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCE_EXPIRING_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value ?? "any"}
                          value={
                            option.value === null ? "any" : String(option.value)
                          }
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Shows virtual machines whose expiry date falls within the
                    window, soonest first. Picking one also switches Type to
                    Virtual Machine and clears the Created Date range.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resourceTypeFilter">Resource Type</Label>
                  <Select
                    value={type}
                    onValueChange={(value) => {
                      setFilter("type", value as ResourceType | "All");
                    }}
                  >
                    <SelectTrigger id="resourceTypeFilter">
                      <SelectValue placeholder="Resource Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCE_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resourceStatusFilter">Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) => {
                      setFilter(
                        "status",
                        value as ResourceStatus | "All" | "Active",
                      );
                    }}
                  >
                    <SelectTrigger id="resourceStatusFilter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCE_LIST_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resourceProductLineFilter">
                    Product Line
                  </Label>
                  <Select
                    value={productLineSlug}
                    onValueChange={handleProductLineChange}
                  >
                    <SelectTrigger id="resourceProductLineFilter">
                      <SelectValue placeholder="Product Line" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_PRODUCT_LINES}>
                        All Product Lines
                      </SelectItem>
                      {productLines.map((line) => (
                        <SelectItem key={line.id} value={line.slug}>
                          {line.name}
                        </SelectItem>
                      ))}
                      {/* A picked line the catalogue does not list - still loading, or renamed
                          since - keeps its own entry, so the trigger never reads blank over an
                          active filter. */}
                      {isFilteringByProductLine && !pickedProductLine && (
                        <SelectItem value={productLineSlug}>
                          {productLineSlug}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {productLinesQuery.isError && (
                    <p className="text-xs text-destructive">
                      Could not load the product lines.{" "}
                      <button
                        type="button"
                        onClick={() => {
                          void productLinesQuery.refetch();
                        }}
                        className="cursor-pointer underline focus:outline-none"
                      >
                        Retry
                      </button>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Matches the line shown under Type, so a machine with none
                    recorded is listed under no line. Picking one also switches
                    Type to Virtual Machine.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resourceCreatedDateFilter">
                    Created Date
                  </Label>
                  <DateRangePicker
                    value={dateRange}
                    onChange={(range) => {
                      setFilter("dateRange", range);
                    }}
                    placeholder="Select date range"
                  />
                </div>
              </PopoverContent>
            </Popover>

            <ColumnSettingsMenu
              columns={RESOURCE_COLUMNS}
              isVisible={(id) => !hiddenColumns.includes(id)}
              toggle={toggleColumn}
              showAll={showAllColumns}
              hiddenCount={hiddenColumns.length}
              className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
            />
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}
          {isLoading ? (
            /* The first load has no rows yet, and the branches below both read an empty list as an
               answer: the table renders its own "No resources found" and the footer states
               "Page 1 of 1 (0 total)". An operator opening the screen was told there was nothing
               here before the request had come back. Skeleton rows say "still loading" instead. */
            <div
              className="min-h-0 flex-1 space-y-2 overflow-hidden rounded-md border p-3"
              data-testid="resource-list-skeleton"
            >
              {Array.from({ length: 8 }, (_, index) => (
                <Skeleton key={index} className="h-11 w-full" />
              ))}
            </div>
          ) : (
            /* Rendered at every row count, empty included: this component owns the action dialogs
               and the busy overlay, so branching around it here tore them off the screen whenever
               a successful action emptied the list — the last restorable VM leaving the
               "Restorable" view, the last row of a page being deleted. The empty wording is still
               this page's to write, because only it knows whether a filter is what emptied the
               list, so it goes down as a prop. */
            <ResourceMonitoringTable
              resources={resources}
              startIndex={(currentPage - 1) * pageSize}
              selectionResetKey={selectionResetKey}
              emptyState={
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
                  {/* Fills the card the way the table does instead of collapsing to its own
                      content. CardContent is a min-h-0 flex column, so a fixed-height block here
                      left the card ending part-way down the screen: the whole panel changed size
                      the moment a filter emptied the list, and changed back when it matched
                      again. justify-center then puts the message in the middle of the space the
                      rows occupied rather than tucked under the toolbar. */}
                  <Server className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
                  <h3 className="mb-2 text-lg font-semibold">
                    {hasActiveFilters
                      ? "No results found"
                      : "No resources found"}
                  </h3>
                  <p className="text-muted-foreground">
                    {hasActiveFilters
                      ? "Try adjusting or clearing your filters."
                      : "Resources will appear here once tenants provision them."}
                  </p>
                </div>
              }
            />
          )}

          {/* The footer sits outside the three branches above, so a search that matched nothing
              still shows its pager, its page-size picker and the reset button. It reads
              "Page 1 of 1 (0 total)" with both arrows disabled - a true statement about a result
              that came back, and the row an operator needs to change the page size or clear the
              filter that emptied the list.

              `!isLoading` is the one case still held back: during the very first load "0 total"
              would describe a request that has not answered yet, which is the same lie the
              skeleton above exists to avoid. */}
          {!isLoading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value));
                  }}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                {/* Rendered at every page count, not just `totalPages > 1`: hiding the pager on a
                      single page made the controls appear and disappear as filters changed, and left
                      the operator with no visible page control at all on a one-page result. Previous
                      and Next disable themselves at the ends, so a single page simply shows both
                      greyed out next to page 1. */}
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isSwitchingView}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isSwitchingView}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isSwitchingView}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Moved down from the toolbar to sit with the other paging controls. The label is
                    deliberately NOT "Refresh": handleRefresh is this screen's "start over" - it
                    drops every filter, the page size and the cached copy in sessionStorage before
                    reloading. With the caption gone this tooltip is the only thing left telling the
                    operator that clicking it throws their filters away, so it says what the click
                    actually does. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isSwitchingView}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
